// Personal Forecast Types - Full personal finance model
// Inspired by CPA spreadsheet: BS (What I Own & Owe) + CF (Cash Flows) + FX

export type Currency = 'CAD' | 'USD' | 'GBP' | 'EUR';
export type AccountBucket = 'current' | 'savings' | 'retirement';
export type AccountSide = 'asset' | 'liability';
export type ViewMode = 'simple' | 'advanced' | 'expert';
export type ValuationMode = 'nominal' | 'real';
export type TimelineHorizon = '1yr' | '5yr' | '10yr' | '20yr' | '30yr' | 'retirement' | 'custom';

export type CashFlowFrequency =
  | 'monthly'
  | 'biweekly'
  | 'semi-monthly'
  | 'weekly'
  | 'annual'
  | 'quarterly'
  | 'one-time';

// Balance Sheet account (What I Own & Owe)
export interface ForecastAccount {
  id: string;
  name: string;
  currency: Currency;
  bucket: AccountBucket;
  side: AccountSide;
  openingBalance: number;
  interestRate: number;   // annual % (savings earn, debts cost)
  growthRate: number;     // annual % (investments)
  /**
   * What share of this balance is yours after tax. 100 for a TFSA or a bank account you have
   * already paid tax on; less for an RRSP, a pension or CPP, where the balance is taxed on the way
   * out. Undefined means 100 - a balance is taken at face value until you say otherwise.
   */
  afterTaxPercent?: number;
  /**
   * What kind of account this is, for tools that need to know - the Retirement Planner treats an
   * RRSP, a TFSA and a taxable account completely differently. Left unset it is inferred from the
   * name and the after-tax share, which is right most of the time and correctable when it isn't.
   */
  accountType?: 'rrsp' | 'tfsa' | 'nonRegistered' | 'property' | 'other';
  sortOrder: number;
  /**
   * Month-end balances you actually had, keyed YYYY-MM. Months up to the latest entry
   * are shown as actuals; the forecast starts from that balance in the following month.
   */
  actuals?: Record<string, number>;
  /** Fixed dollar change each forecast month, on top of any growth: +500 for a revaluation, -1000 for a depreciating car. */
  monthlyChange?: number;
  /**
   * Derived balance: `percent` of the summed balances of `accountIds` in the same month,
   * for example selling costs as 12% of the houses. Overrides growth, interest and flows.
   */
  linkedTo?: { accountIds: string[]; percent: number };
  /**
   * In the forecast, if last month's balance was above `aboveBalance`, a fixed `transferAmount`
   * moves out to `toAccountId` this month too (sweep excess chequing into a TFSA once it builds
   * up past a buffer). Checked against last month so there's no same-month circularity between
   * the two accounts. Source and destination should be the same currency and both assets;
   * the sweep is a pure reallocation and never touches net worth or total assets.
   */
  /**
   * Move money out once this account has more than it needs. Either a fixed threshold and amount,
   * or - with keepMonthsOfSpending - keep that many months of your actual spending here and move
   * the whole excess, so a chequing account cannot quietly accumulate for thirty years.
   */
  sweepRule?: {
    aboveBalance: number;
    transferAmount: number;
    toAccountId: string;
    keepMonthsOfSpending?: number;
  };
}

// Cash Flow item (income or expense linked to an account)
export interface CashFlowItem {
  id: string;
  name: string;
  accountId: string;      // which account this flows through
  amount: number;         // per-period amount
  frequency: CashFlowFrequency;
  startDate: string;      // YYYY-MM
  endDate: string;        // YYYY-MM, empty = ongoing
  annualGrowthRate: number;
  /**
   * Salary, Housing, Food - or one of the user's own, managed in Settings. A category of their own
   * reports as a single net line with its own schedule, because a rental's rent, costs, interest
   * and principal belong together and no built-in category can hold them.
   */
  category: string;
  isInflow: boolean;      // true = money in, false = money out
  sortOrder: number;
  firstPayDate: string;   // biweekly: first pay date for 3-paycheck calc
  /**
   * 1-12. When set, annualGrowthRate is applied as a step each time this month comes round
   * after startDate (a raise effective every August), instead of smooth monthly compounding.
   */
  growthMonth?: number;
  /** 1-12. When set, the flow only happens in these calendar months (seasonal income or costs). */
  activeMonths?: number[];
  /**
   * When set, this is a transfer: the amount leaves `accountId` and arrives in `toAccountId`
   * in the same month (an RESP contribution, a loan payment). `isInflow` is ignored.
   */
  toAccountId?: string;
  /**
   * When set, the amount each month is `percent` of what the referenced items paid that
   * month (a pension contribution as a share of salary and bonus). annualGrowthRate is
   * ignored here; start, end and active months still apply. See growthPhaseYears below for
   * how this interacts with a growth phase.
   */
  percentOf?: { itemIds: string[]; percent: number };
  /**
   * When set, the amount each month is this annual rate on an account's opening balance - mortgage
   * interest as 3.3% of what's left owing. It falls as the balance falls and reaches zero when the
   * balance does, so it needs no end date and can't charge interest on a debt you've paid off.
   */
  percentOfBalance?: { accountId: string; annualRate: number };
  /**
   * Years after startDate (or, for a "% of" item with no startDate, after the forecast
   * begins) before growth switches from annualGrowthRate to longTermGrowthRate. Lifestyle
   * creep for the first 5 years, then held to inflation, say.
   *
   * On a plain item this just changes which rate compounds from that point on; the amount
   * never resets. On a "% of" item it instead ends the link: the month before the switch is
   * its last one tracking its sources, and from the switch month on it freezes at what they
   * produced then and grows on its own at longTermGrowthRate (a spending habit outgrows
   * tying itself to a paycheque forever).
   */
  growthPhaseYears?: number;
  /** The rate that takes over after growthPhaseYears. Falls back to annualGrowthRate if unset. */
  longTermGrowthRate?: number;
  /**
   * What this item actually moved in recorded months, keyed YYYY-MM. Inside history the
   * forecast rules are not applied; these amounts are what the Cash Flow views show there.
   */
  actuals?: Record<string, number>;
}

// FX rate between currencies
export interface FXRate {
  fromCurrency: Currency;
  toCurrency: Currency;
  rate: number;
  asOfDate: string;       // YYYY-MM-DD
}

// User settings for the forecast
export interface ForecastSettings {
  baseCurrency: Currency;
  inflationRate: number;  // annual %
  horizon: TimelineHorizon;
  customMonths: number;   // when horizon = 'custom'
  retirementDate: string; // YYYY-MM, when horizon = 'retirement'
  valuationMode: ValuationMode;
  viewMode: ViewMode;
  showFX: boolean;
  /** YYYY-MM the balance history grid starts from. Empty = no history tracked. */
  historyStartMonth: string;
  /** Birth year, so every year can be labelled with the age you reach in it. 0 = not set. */
  birthYear: number;
  /**
   * Every category this scenario offers, the starter names included - they are names, not special
   * cases, so all of them can be renamed or removed. Older scenarios have no list and fall back to
   * the starters plus whatever they were already using; see resolveCategories.
   */
  categories?: string[];
  /** Insight ids marked seen. They are still worked out, just folded away until asked for. */
  dismissedInsights?: string[];
  /** Superseded by `categories`. Kept so scenarios saved before the merge still load. */
  customCategories?: string[];
}

// Full scenario (what gets saved/loaded)
export interface ForecastScenario {
  id: string;
  name: string;
  accounts: ForecastAccount[];
  cashFlows: CashFlowItem[];
  fxRates: FXRate[];
  settings: ForecastSettings;
  createdAt: string;
  updatedAt: string;
}

// --- Projection outputs ---

export interface MonthlyAccountProjection {
  openingBalance: number;
  inflows: number;
  outflows: number;
  interest: number;
  growth: number;
  /** The account's fixed monthly change applied this month: a house revaluation, a car depreciating.
   *  Passive income in dollars rather than a percent, so it sits alongside interest and growth. */
  valuationChange: number;
  closingBalance: number;
  /** True when closingBalance came from the user's recorded history, not the projection. */
  isActual: boolean;
}

export interface CurrencySubtotal {
  totalAssets: number;
  totalLiabilities: number;
  netWorth: number;
  totalInflows: number;
  totalOutflows: number;
}

export interface MonthlyProjection {
  month: string; // YYYY-MM
  /** 'actual' up to the latest month any account has a recorded balance, then 'forecast'. */
  source: 'actual' | 'forecast';
  accounts: Record<string, MonthlyAccountProjection>;
  /** Amount each cash-flow item contributed this month, keyed by item id, in the item's account currency. */
  flows: Record<string, number>;
  /**
   * Movements a sweep rule made this month. These are not cash flow items, so anything that reads
   * `flows` alone would miss them - which is how the Cash Flow view's bank line came to overstate
   * cash by the whole sweep once the rule started firing.
   */
  sweepMoves: { fromAccountId: string; toAccountId: string; amount: number }[];
  totalInflows: number;
  totalOutflows: number;
  netCashFlow: number;
  totalAssets: number;
  totalLiabilities: number;
  netWorth: number;
  /** Net worth with every taxable balance cut to its after-tax share. Debts come off in full. */
  netWorthAfterTax: number;
  /** What your assets earned this month: interest and growth on what you own. Interest on a debt
   *  is a cost, not income, so it is left out. */
  passiveIncome: number;
  /** passiveIncome against what you actually spent - transfers to your own accounts excluded,
   *  because moving money is not a cost of living. */
  passiveIncomeRatio: number;
  /** Raw per-currency figures, unconverted. */
  byCurrency: Record<Currency, CurrencySubtotal>;
  // totalAssets, totalLiabilities and netWorth above are in the base currency.
  // These three repeat them under their older names for components that still read them.
  combinedAssets: number;
  combinedLiabilities: number;
  combinedNetWorth: number;
  warnings: string[];
}

// --- Biweekly helpers (kept from old code) ---

export interface BiweeklyPaySchedule {
  frequency: 'biweekly';
  firstPayDate: string;
  paycheckAmount: number;
  annualPaychecks: 26;
  monthlyBreakdown: Array<{
    month: string;
    paychecks: number;
    totalAmount: number;
    isExtraPaycheckMonth: boolean;
  }>;
}

// --- Defaults ---

export const CF_CATEGORIES = [
  'Salary',
  'Bonus',
  'Pension',
  'Investment Income',
  'Rental Income',
  'Other Income',
  'Housing',
  'Utilities',
  'Transportation',
  'Food',
  'Entertainment',
  'Insurance',
  'Debt Payments',
  'Taxes',
  'Savings Contribution',
  'Other',
] as const;

export const DEFAULT_FORECAST_SETTINGS: ForecastSettings = {
  baseCurrency: 'CAD',
  inflationRate: 2.0,
  horizon: '5yr',
  customMonths: 60,
  retirementDate: '',
  valuationMode: 'nominal',
  viewMode: 'simple',
  showFX: false,
  historyStartMonth: '',
  birthYear: 0,
  categories: [...CF_CATEGORIES],
};

export const CURRENCIES: Currency[] = ['CAD', 'USD', 'GBP', 'EUR'];

export const CURRENCY_SYMBOLS: Record<Currency, string> = {
  CAD: '$',
  USD: '$',
  GBP: '\u00A3',
  EUR: '\u20AC',
};

export const ACCOUNT_BUCKETS: { value: AccountBucket; label: string }[] = [
  { value: 'current', label: 'Current' },
  { value: 'savings', label: 'Savings' },
  { value: 'retirement', label: 'Retirement' },
];

export type CFCategory = string;

/**
 * The categories a scenario offers. A scenario saved before the starter list and the user's own
 * were merged has no list of its own, so build one: the starters, anything it used to call custom,
 * and any category its items already carry, so nothing in use is ever missing from the dropdown.
 */
export const resolveCategories = (
  settings: Pick<ForecastSettings, 'categories' | 'customCategories'>,
  cashFlows: { category: string }[] = [],
): string[] => {
  if (settings.categories && settings.categories.length) return settings.categories;
  const seen = new Set<string>([...CF_CATEGORIES, ...(settings.customCategories || [])]);
  for (const cf of cashFlows) if (cf.category) seen.add(cf.category);
  return Array.from(seen);
};


// --- Cash flow by category over time ---

export type FlowPeriod = 'month' | 'year';

/** One line item inside a category, signed the way cash moves: positive in, negative out. */
export interface CategoryLineItem {
  label: string;
  isInflow: boolean;
  /** Mortgage principal, a contribution: cash leaves but net worth doesn't move. */
  isTransfer: boolean;
  byPeriod: Record<string, number>;
  total: number;
}

/**
 * One category, netted. Every category behaves the same way - CF_CATEGORIES is a list of starting
 * points, not a set of special cases - so a rental you named yourself and Food are the same shape.
 *
 * Cash and net worth are reported separately because for anything mortgaged they differ: paying
 * principal takes cash but swaps it for equity. And money that never touches a current account
 * (an employer contribution landing straight in an RRSP) is kept out of netCash, or the forecast's
 * net cash flow would claim money arrived in the bank that never did.
 */
export interface CategoryFlowRow {
  category: string;
  /** Its line items, so a category with several earns a schedule of its own. */
  items: CategoryLineItem[];
  /** Movement through current accounts: what actually hit the bank. */
  netCash: Record<string, number>;
  /** Movement that never touched cash - contributions arriving straight in savings or retirement. */
  netNonCash: Record<string, number>;
  /** Everything that changed net worth: inflows less spending, ignoring transfers between your own accounts. */
  netWorth: Record<string, number>;
  /** The slice of outflows that only moved money you still own - principal, a contribution. */
  transfersOut: Record<string, number>;
  total: number;
}

export interface CategoryFlowTable {
  periods: string[];
  /** One row per category, whether it came from the starter list or the user added it. */
  rows: CategoryFlowRow[];
  /** Net movement through current accounts each period - the forecast's own net cash flow. */
  cashNet: Record<string, number>;
  /**
   * Every account, not just the current ones, with transfers between your own accounts cancelling.
   * Money arriving in a retirement account counts: it is still money you earned. This is what the
   * category lines add up to, and it equals the change in net worth that flows account for.
   */
  netTotal: Record<string, number>;
}

/** One account's share of growth in the income statement: interest, the growth rate, a fixed
 * monthly change and any debt-floor clamp, combined and signed so an asset's gain is positive
 * and a liability's gain (a paydown) is also positive - every line reads as its effect on net worth. */
export interface IncomeStatementRow {
  label: string;
  byPeriod: Record<string, number>;
  total: number;
}

/**
 * Income split the way it's earned: active (net cash flow plus whatever moved into savings,
 * retirement or debt paydown - a pure transfer cancels across the two) and passive (each account's
 * interest, growth and revaluation, plus a reconciling line for balance moves nothing else explains).
 * Built to reconcile exactly to the change in net worth each period.
 */
export interface IncomeStatement {
  periods: string[];
  netCashFlow: Record<string, number>;
  netInvesting: Record<string, number>;
  passiveRows: IncomeStatementRow[];
  /** Recorded balances that stepped with no flow, rate or revaluation behind them - a restatement
   *  or a missing entry. Passive income by default, but a large figure here means the data is off. */
  otherReconciling: Record<string, number>;
  /** Undefined for the very first period in the timeline: there's nothing before it to compare against. */
  changeInNetWorth: Record<string, number | undefined>;
}

/** One calendar year of projections rolled up: year-end balances, summed flows. */
export interface YearSummary {
  year: string;
  /** Months of this year inside the timeline (12 unless the timeline starts or ends mid-year). */
  months: number;
  firstMonth: string;
  lastMonth: string;
  /** Index into the monthly projections of this year's last month, for real-dollar deflation. */
  lastIndex: number;
  source: 'actual' | 'forecast' | 'mixed';
  accounts: Record<string, MonthlyAccountProjection>;
  totalInflows: number;
  totalOutflows: number;
  netCashFlow: number;
  totalAssets: number;
  totalLiabilities: number;
  netWorth: number;
  /** Year-end net worth with taxable balances cut to their after-tax share. */
  netWorthAfterTax: number;
  /** Change from the previous year's year-end; undefined for the first year. */
  netWorthChange?: number;
  passiveIncomeRatio: number;
  warnings: string[];
}
